import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

const CourseSummary = (props) => {
  const { sections, currentSection, currentLesson } = props;

  const [openId, setOpenId] = useState(currentSection.id);

  const toggleSection = (id) => {
    if (openId === id) {
      setOpenId(null);
    } else {
      setOpenId(id);
    }
  };

  const currentProgress = (section) => {
    const indexOfCurrentLesson = section.lessons.findIndex(
      (lesson) => lesson.id === currentLesson.id,
    );

    if (indexOfCurrentLesson === -1) {
      return 0;
    }
    return indexOfCurrentLesson + 1;
  };

  const isActiveLesson = (lesson) => {
    return lesson.id === currentLesson.id;
  };

  const isLessonDone = (lesson) => {
    const lessonIndex = currentSection.lessons.findIndex(
      (l) => l.id === lesson.id,
    );

    if (lessonIndex === -1) {
      return false;
    }

    return (
      lessonIndex <
      currentSection.lessons.findIndex((l) => l.id === currentLesson.id)
    );
  };

  useEffect(() => {
    setOpenId(currentSection.id);
  }, [currentSection, currentLesson]);

  const LessonIcon = ({ isDone, lesson }) => {
    if (isDone) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m4.5 12.75 6 6 9-13.5"
          />
        </svg>
      );
    }

    if (lesson.lesson_type === "video") {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
          />
        </svg>
      );
    }

    if (lesson.lesson_type === "test") {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
          />
        </svg>
      );
    }

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        className="size-5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25"
        />
      </svg>
    );
  };

  return (
    <div className="w-full max-w-screen lg:max-w-xs max-h-[50vh] lg:max-h-[75vh] overflow-y-auto">
      <div className="w-full">
        {sections.map((section) => {
          const isOpen = openId == section.id;
          return (
            <div key={section.id} className="overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-200"
                aria-expanded={isOpen}
              >
                <div className="flex flex-col text-left">
                  <span className="text-sm font-semibold text-gray-800">
                    {section.title}
                  </span>
                  <span className="text-xs text-gray-500">
                    {currentProgress(section)}/{section.lessons.length}
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500" />
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </button>

              {/* Content */}
              <div
                className={`border-t border-gray-200 ${
                  isOpen ? "block" : "hidden"
                }`}
              >
                {section.lessons.map((lesson) => {
                  const isDone = isLessonDone(lesson);
                  const isActive = isActiveLesson(lesson);

                  const baseTextColor =
                    isActive || isDone ? "text-gray-900" : "text-gray-400";

                  const rowBg = isActive ? "bg-primaryYellow500" : "bg-white";

                  return (
                    <div
                      key={lesson.id}
                      className={`flex items-center justify-between px-4 py-3 text-sm ${rowBg}`}
                    >
                      <div
                        className={`flex items-center space-x-3 w-full h-full ${baseTextColor} `}
                      >
                        <LessonIcon isDone={isDone} lesson={lesson} />
                        <span className="font-medium">{lesson.title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

CourseSummary.propTypes = {
  sections: PropTypes.array,
  currentSection: PropTypes.object,
  currentLesson: PropTypes.object,
};

export default CourseSummary;
